import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { apiClient } from '../api';

interface HeaderProps {
  onLogout?: () => void;
}

interface UserProfile {
  username: string;
  user_id: string;
  role: string;
  avatar_path?: string;
  first_name?: string;
  last_name?: string;
}

const Header: React.FC<HeaderProps> = ({ onLogout }) => {
  const { state, actions, getCurrentStats } = useApp();
  const location = useLocation();
  const navigate = useNavigate();
  const [userProfile, setUserProfile] = React.useState<UserProfile | null>(null);
  const [showUserMenu, setShowUserMenu] = React.useState(false);
  const userMenuRef = React.useRef<HTMLLIElement>(null);
  
  // Get unread count from dashboard stats (updated via delta sync)
  const stats = getCurrentStats();
  const unreadCount = stats?.unread_count || 0;

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    };

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showUserMenu]);

  // Fetch current user profile
  React.useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await apiClient.get('/profile');
        setUserProfile(response.data);
      } catch (error) {
        console.error('Failed to fetch user profile:', error);
      }
    };
    fetchProfile();
  }, []);

  const getStatusBadgeClass = () => {
    switch (state.wsStatus) {
      case 'CONNECTED':
        return 'badge-success';
      case 'CONNECTING':
        return 'badge-secondary';
      case 'DISCONNECTED':
        return 'badge-warning';
      case 'ERROR':
        return 'badge-danger';
      default:
        return 'badge-secondary';
    }
  };

  const getStatusText = () => {
    switch (state.wsStatus) {
      case 'CONNECTED':
        return 'CONNECTED';
      case 'CONNECTING':
        return 'CONNECTING';
      case 'DISCONNECTED':
        return 'DISCONNECTED';
      case 'ERROR':
        return 'ERROR';
      default:
        return 'UNKNOWN';
    }
  };

  const getChannelHealthText = () => {
    if (!stats?.channels) return 'CHECKING';
    
    const healthyChannels = stats.channels.filter(r => r.last_status === 'success').length;
    const totalChannels = stats.channels.length;
    
    if (totalChannels === 0) return 'NO ROUTES';
    if (healthyChannels === totalChannels) return 'ALL ACTIVE';
    if (healthyChannels === 0) return 'ALL INACTIVE';
    return `${healthyChannels}/${totalChannels} ACTIVE`;
  };

  const getChannelHealthBadgeClass = () => {
    if (!stats?.channels) return 'badge-info';
    
    const healthyChannels = stats.channels.filter(r => r.last_status === 'success').length;
    const totalChannels = stats.channels.length;
    
    if (totalChannels === 0) return 'badge-secondary';
    if (healthyChannels === totalChannels) return 'badge-success';
    if (healthyChannels === 0) return 'badge-danger';
    return 'badge-warning';
  };

  const scrollToSection = (sectionId: string) => {
    // If we're not on dashboard, navigate there first
    if (location.pathname !== '/dashboard') {
      navigate('/dashboard');
      setTimeout(() => {
        const element = document.querySelector(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      const element = document.querySelector(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const getPageTitle = () => {
    switch (location.pathname) {
      case '/dashboard':
        return 'Affinidi Trust Fabric Gateway - Dashboard';
      case '/identities':
        return 'Affinidi Trust Fabric Gateway - Identities';
      case '/channels':
        return 'Affinidi Trust Fabric Gateway - Channels';
      case '/gateways':
        return 'Affinidi Trust Fabric Gateway - Linked Gateways';
      case '/integrations':
        return 'Affinidi Trust Fabric Gateway - Integrations';
      case '/mediators':
        return 'Affinidi Trust Fabric Gateway - Mediators';
      case '/mcp-proxies':
        return 'Affinidi Trust Fabric Gateway - MCP Proxies';
      case '/trust-registries':
        return 'Affinidi Trust Fabric Gateway - Trust Registries';
      case '/secrets':
        return 'Affinidi Trust Fabric Gateway - Secrets';
      case '/users':
        return 'Affinidi Trust Fabric Gateway - Users';
      case '/profile':
        return 'Affinidi Trust Fabric Gateway - Profile';
      case '/notifications':
        return 'Affinidi Trust Fabric Gateway - Notifications';
      case '/tasks':
        return 'Affinidi Trust Fabric Gateway - Tasks';
      case '/settings':
        return 'Affinidi Trust Fabric Gateway - Settings';
      case '/logs':
        return 'Affinidi Trust Fabric Gateway - Logs';
      case '/metrics':
        return 'Affinidi Trust Fabric Gateway - Metrics';
      default:
        // Handle dynamic routes with pattern matching
        if (location.pathname.startsWith('/channels/')) {
          if (location.pathname.endsWith('/capture')) {
            return 'Affinidi Trust Fabric Gateway - Channel Capture';
          }
          return 'Affinidi Trust Fabric Gateway - Edit Channel';
        }
        if (location.pathname.startsWith('/gateways/')) {
          if (location.pathname.endsWith('/publish')) {
            return 'Affinidi Trust Fabric Gateway - Publish Gateway';
          }
          if (location.pathname.endsWith('/connect')) {
            return 'Affinidi Trust Fabric Gateway - Connect Gateway';
          }
          return 'Affinidi Trust Fabric Gateway - Edit Gateway';
        }
        if (location.pathname.startsWith('/mediators/')) {
          if (location.pathname.endsWith('/wizard')) {
            return 'Affinidi Trust Fabric Gateway - Add Mediator';
          }
          return 'Affinidi Trust Fabric Gateway - Edit Mediator';
        }
        if (location.pathname.startsWith('/mcp-proxies/')) {
          if (location.pathname.endsWith('/wizard')) {
            return 'Affinidi Trust Fabric Gateway - Add MCP Proxy';
          }
          return 'Affinidi Trust Fabric Gateway - Edit MCP Proxy';
        }
        if (location.pathname.startsWith('/trust-registries/')) {
          if (location.pathname.endsWith('/wizard')) {
            return 'Affinidi Trust Fabric Gateway - Add Trust Registry';
          }
          return 'Affinidi Trust Fabric Gateway - Edit Trust Registry';
        }
        if (location.pathname.startsWith('/users/')) {
          return 'Affinidi Trust Fabric Gateway - Edit User';
        }
        if (location.pathname.startsWith('/notifications/')) {
          return 'Affinidi Trust Fabric Gateway - Edit Notification';
        }
        if (location.pathname === '/onboard') {
          return 'Affinidi Trust Fabric Gateway - Onboard';
        }
        return 'Affinidi Trust Fabric Gateway - Dashboard';
    }
  };

  return (
    <nav className="navbar navbar-expand navbar-light bg-white topbar mb-4 static-top shadow">
      {/* Sidebar Toggle (Topbar) */}
      <button 
        type="button"
        title='sidebar-toggle'
        id="sidebarToggleTop" 
        className="btn btn-link d-md-none rounded-circle mr-3"
        onClick={actions.toggleSidebar}
      >
        <i className="fa fa-bars"></i>
      </button>

      {/* Topbar Title */}
      <h1 className="h3 mb-0 text-gray-800 mr-auto" id="page-title">
        {getPageTitle()}
      </h1>

      {/* Topbar Navbar */}
      <ul className="navbar-nav ml-auto align-items-center">
        {/* Theme Toggle */}
        <li className="nav-item mr-3">
          <button 
            id="themeToggle" 
            className="btn btn-sm btn-outline-secondary" 
            title="Toggle Dark Mode"
            onClick={actions.toggleTheme}
          >
            <i className={`fas ${state.theme === 'dark' ? 'fa-sun' : 'fa-moon'}`} id="themeIcon"></i>
          </button>
        </li>
        
        {/* Status Badge */}
        <li className="nav-item">
          <span 
            className={`badge ${getStatusBadgeClass()} px-3 py-2`}
            id="ws-status-badge" 
            onClick={() => navigate('/dashboard')} 
            style={{ cursor: 'pointer' }} 
            title="Click to view dashboard"
          >
            <i className="fas fa-circle"></i> <span id="ws-status-text">{getStatusText()}</span>
          </span>
        </li>
        
        {/* Channel Health Badge */}
        <li className="nav-item ml-2">
          <span 
            className={`badge ${getChannelHealthBadgeClass()} px-3 py-2`}
            id="channel-status-badge" 
            onClick={() => navigate('/channels')} 
            style={{ cursor: 'pointer', width: '130px', display: 'inline-block', textAlign: 'center' }} 
            title="Click to view channels"
          >
            <i className="fas fa-channel"></i> <span id="channel-status-text">{getChannelHealthText()}</span>
          </span>
        </li>

        {/* Notifications Badge */}
        <li className="nav-item ml-2">
          <button 
            className="btn btn-sm btn-outline-primary position-relative"
            onClick={() => navigate('/notifications')}
            title="View notifications"
            style={{ padding: '0.5rem 1rem' }}
          >
            <i className="fas fa-bell"></i>
            {unreadCount > 0 && (
              <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger" style={{ fontSize: '0.7rem' }}>
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </button>
        </li>

        {/* User Avatar & Menu */}
        {onLogout && (
          <li className="nav-item dropdown ml-3" ref={userMenuRef}>
            <button
              className="btn btn-link nav-link dropdown-toggle p-0"
              onClick={() => setShowUserMenu(!showUserMenu)}
              style={{
                border: 'none',
                background: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              {userProfile?.avatar_path ? (
                <img
                  src={`/${userProfile.avatar_path}`}
                  alt="User avatar"
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    objectFit: 'cover',
                    border: '2px solid #4e73df',
                  }}
                />
              ) : (
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    backgroundColor: '#4e73df',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '18px',
                    fontWeight: 'bold',
                  }}
                >
                  <i className="fas fa-user"></i>
                </div>
              )}
              {userProfile && (
                <span className="d-none d-lg-inline text-gray-600 small">
                  {userProfile.first_name || userProfile.username}
                </span>
              )}
            </button>
            {showUserMenu && (
              <div
                className="dropdown-menu dropdown-menu-right shadow animated--grow-in show"
                style={{
                  position: 'absolute',
                  right: 0,
                  marginTop: '0.5rem',
                  minWidth: '200px',
                }}
              >
                <button
                  className="dropdown-item"
                  onClick={() => {
                    setShowUserMenu(false);
                    navigate('/profile');
                  }}
                  style={{
                    padding: '0.75rem 1.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    lineHeight: '1.5',
                  }}
                >
                  <i className="fas fa-user fa-sm fa-fw mr-2 text-gray-400"></i>
                  Profile
                </button>
                <div className="dropdown-divider"></div>
                <button
                  className="dropdown-item"
                  onClick={() => {
                    setShowUserMenu(false);
                    onLogout();
                  }}
                  style={{
                    padding: '0.75rem 1.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    lineHeight: '1.5',
                  }}
                >
                  <i className="fas fa-sign-out-alt fa-sm fa-fw mr-2 text-gray-400"></i>
                  Sign Out
                </button>
              </div>
            )}
          </li>
        )}
      </ul>
    </nav>
  );
};

export default Header;